// ./dialogs/PaymentDialog.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import MobileDialog from './MobileDialog';
import PaymentMethodRows from '../components/PaymentMethodRows';
import API from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';

// Fetch clients with server-side search
async function fetchClientsAutocomplete(searchTerm = '') {
  const params = {
    light: true,
    limit: 100
  };
  if (searchTerm) {
    params.search = searchTerm;
  }
  const response = await API.get('/clients/', { params });
  return response.data;
}

const PaymentDialog = ({
  open,
  onClose,
  debtFields = [],
  debtFieldsSchema = {},
  fetchTransactions,
  invalidateClientListCache,
  userProfile,
  initialClient = null,
}) => {
  // Client selection
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDebt, setClientDebt] = useState({});

  // Server-side autocomplete search state
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerRef = useRef(null);

  // React Query for server-side client search
  const { data: autocompleteClients = [], isLoading: isLoadingAutocomplete } = useQuery({
    queryKey: ['clients', 'autocomplete', 'payment', debouncedAutocompleteSearch],
    queryFn: () => fetchClientsAutocomplete(debouncedAutocompleteSearch),
    enabled: open,  // Only fetch when dialog is open
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Create options with "no results" indicator when empty and user is searching
  const autocompleteOptions = useMemo(() => {
    if (autocompleteClients.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return autocompleteClients;
  }, [autocompleteClients, debouncedAutocompleteSearch]);

  // Debounced callback for autocomplete search
  const debouncedSetAutocompleteSearch = useCallback((value) => {
    if (autocompleteTimerRef.current) {
      clearTimeout(autocompleteTimerRef.current);
    }
    autocompleteTimerRef.current = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
  }, []);

  // Payment details
  const [amount, setAmount] = useState('');
  const [invoice, setInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [actualDate, setActualDate] = useState(new Date());
  const [fieldValues, setFieldValues] = useState({});

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  // Extra fields state
  const [extraFieldValues, setExtraFieldValues] = useState({});
  
  // Form state
  const [formStep, setFormStep] = useState('form');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Memoize configuration values to prevent infinite render loops
  const uiConfig = useMemo(() => userProfile?.ui_config || {}, [userProfile]);
  const transactionFields = useMemo(() => uiConfig.transaction_fields || [], [uiConfig]);
  const transactionFieldsSchema = useMemo(() => uiConfig.transaction_fields_schema || {}, [uiConfig]);
  
  // Check if Invoice field is available
  const hasInvoiceField = useMemo(() => transactionFields.includes('Invoice'), [transactionFields]);
  
  // Standard fields that are always part of transactions
  const standardFields = useMemo(() => 
    ['TrID', 'Type', 'SubType', 'Amount', 'TotalAfterTR', 'Timestamp', 'Invoice', 'Note'], 
    []
  );
  
  // Extract extra fields by comparing to standard fields - memoize to prevent re-renders
  const extraFields = useMemo(() => 
    transactionFields.filter(field => 
      !standardFields.includes(field) && field !== 'TrID' && field !== 'Timestamp'
    ),
    [transactionFields, standardFields]
  );

  // Check if there's only one payment category (excluding TotalDebt)
  const singleCategory = useMemo(() => {
    const paymentCategories = debtFields.filter(field => field !== 'TotalDebt');
    return paymentCategories.length === 1 ? paymentCategories[0] : null;
  }, [debtFields]);

  // Initialize payment field values only once when debtFields changes
  useEffect(() => {
    if (debtFields && debtFields.length > 0) {
      const initialValues = {};
      debtFields.forEach(field => {
        if (field !== 'TotalDebt') {
          initialValues[field] = '0';
        }
      });
      setFieldValues(initialValues);
    }
  }, [debtFields]);
  
  // Initialize extra fields with empty values only once when extraFields changes
  useEffect(() => {
    if (extraFields && extraFields.length > 0) {
      setExtraFieldValues(prev => {
        // Skip update if extraFields is already initialized
        if (Object.keys(prev).length === extraFields.length && 
            extraFields.every(field => field in prev)) {
          return prev;
        }
        
        const initialExtraValues = {};
        extraFields.forEach(field => {
          initialExtraValues[field] = prev[field] || '';
        });
        return initialExtraValues;
      });
    }
  }, [extraFields]);

  // Pre-select client when initialClient is provided and dialog opens
  useEffect(() => {
    if (open && initialClient && !selectedClient) {
      setSelectedClient(initialClient);
    }
  }, [open, initialClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch client debt when client is selected
  useEffect(() => {
    const loadDebt = async () => {
      if (!selectedClient) {
        setClientDebt({});
        return;
      }
      try {
        const resp = await API.get(
          `/client?business_id=${selectedClient.ClientBusinessID}`
        );
        setClientDebt(resp.data.debt?.DebtFields || {});
      } catch (err) {
        console.error('Error fetching client debt for Payment:', err);
      }
    };
    loadDebt();
  }, [selectedClient]);

  // Scroll to error when it appears
  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [localError]);

  // Reset form state
  const resetForm = () => {
    setSelectedClient(null);
    setAmount('');
    setInvoice('');
    setNotes('');
    setActualDate(new Date());

    // Reset field values
    const initialValues = {};
    debtFields.forEach(field => {
      if (field !== 'TotalDebt') {
        initialValues[field] = '0';
      }
    });
    setFieldValues(initialValues);

    // Reset extra field values
    const initialExtraValues = {};
    extraFields.forEach(field => {
      initialExtraValues[field] = '';
    });
    setExtraFieldValues(initialExtraValues);

    // Reset payment methods
    setPaymentMethods([]);

    setFormStep('form');
    setLocalError('');
    setLocalSuccess('');
    setClientDebt({});

    // Reset autocomplete search state
    setDebouncedAutocompleteSearch('');
    if (autocompleteTimerRef.current) {
      clearTimeout(autocompleteTimerRef.current);
    }
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Update a payment field value
  const handleFieldChange = (field, value) => {
    setFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Update an extra field value
  const handleExtraFieldChange = (field, value) => {
    setExtraFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if field is read-only
  const isFieldReadOnly = (field) => {
    const schema = transactionFieldsSchema[field] || {};
    return schema.readonly === true;
  };

  // Validate form before moving to confirmation step
  const handleValidation = () => {
    setLocalError('');
    setLocalSuccess('');
    
    if (!selectedClient) {
      setLocalError('שגיאה: יש לבחור לקוח לפני ביצוע תשלום.');
      return;
    }
    
    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('שגיאה: סכום התשלום חייב להיות גדול מאפס.');
      return;
    }
    
    // Calculate sum of all payment fields
    const separation = {};
    let sumSeparation = 0;
    
    if (singleCategory) {
      // If there's only one category, put the full amount there
      separation[singleCategory] = parsedAmount;
      sumSeparation = parsedAmount;
    } else {
      debtFields.forEach(field => {
        if (field !== 'TotalDebt') {
          const fieldValue = parseFloat(fieldValues[field] || '0');
          separation[field] = fieldValue;
          sumSeparation += fieldValue;
        }
      });

      if (Math.abs(sumSeparation - parsedAmount) > 0.01) { // Allow small floating point error
        setLocalError('שגיאה: סכום החלוקה אינו תואם לסכום הכולל.');
        return;
      }
    }

    // Validate payment methods if provided
    if (paymentMethods.length > 0) {
      const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      if (Math.abs(pmTotal - parsedAmount) > 0.01) {
        setLocalError('שגיאה: סכום אמצעי התשלום אינו תואם לסכום הכולל.');
        return;
      }

      // Check that all check payment methods have check number
      const invalidChecks = paymentMethods.filter(
        pm => pm.type === 'check' && !pm.check_number
      );
      if (invalidChecks.length > 0) {
        setLocalError('שגיאה: יש להזין מספר צ\'ק לכל תשלום בצ\'ק.');
        return;
      }
    }

    setFormStep('confirm');
  };

  // Submit payment
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');
    
    try {
      const parsedAmount = parseFloat(amount);
      
      // Prepare separation object from payment fields
      const separation = {};
      if (singleCategory) {
        // If there's only one category, put the full amount there
        separation[singleCategory] = parsedAmount;
      } else {
        debtFields.forEach(field => {
          if (field !== 'TotalDebt') {
            separation[field] = parseFloat(fieldValues[field] || '0');
          }
        });
      }
      
      // Create the payload with required fields
      const payload = {
        business_id: selectedClient.ClientBusinessID,
        amount: parsedAmount,
        notes: notes,
        payment_seperation: separation,
        invoice: invoice,
      };

      // Add actual_date if set and different from default
      if (actualDate) {
        payload.actual_date = actualDate.toISOString();
      }

      // Add extra fields to the payload
      extraFields.forEach(field => {
        if (extraFieldValues[field] !== '') {
          payload[field] = extraFieldValues[field];
        }
      });

      // Add payment methods if provided
      if (paymentMethods.length > 0) {
        payload.payment_methods = paymentMethods.map(pm => ({
          type: pm.type,
          amount: parseFloat(pm.amount) || 0,
          // Check fields
          check_number: pm.check_number || '',
          check_bank_name: pm.check_bank_name || '',
          check_account_number: pm.check_account_number || '',
          check_due_date: pm.check_due_date ? format(pm.check_due_date, 'yyyy-MM-dd') : null,
          // Card fields
          card_last_four: pm.card_last_four || '',
          card_type: pm.card_type || '',
          // Transfer fields
          bank_name: pm.bank_name || '',
          bank_account_last4: pm.bank_account_last4 || '',
          transfer_date: pm.transfer_date ? format(pm.transfer_date, 'yyyy-MM-dd') : null,
          notes: pm.notes || '',
        }));
      }
      
      await API.post('/client/payment/', payload);
      setLocalSuccess('התשלום בוצע בהצלחה!');
      
      if (invalidateClientListCache) {
        invalidateClientListCache();
      }
      
      if (fetchTransactions) {
        fetchTransactions();
      }
      
      // Close dialog after delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בתשלום:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת ביצוע התשלום'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.4rem' }}>
        טופס תשלום
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localError && (
            <Alert ref={errorRef} severity="error">{localError}</Alert>
          )}
          {localSuccess && (
            <Alert severity="success">{localSuccess}</Alert>
          )}

          {formStep === 'form' && (
            <>
              <Autocomplete
                options={autocompleteOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.isNoResults) return option.ClientName;
                  return option.ClientName
                    ? `${option.ClientName} (${option.ClientBusinessID})`
                    : '';
                }}
                getOptionDisabled={(option) => option.isNoResults === true}
                filterOptions={(x) => x}  // Disable client-side filtering (server does it)
                loading={isLoadingAutocomplete}
                loadingText="טוען..."
                openOnFocus
                value={selectedClient}
                onInputChange={(event, newInputValue, reason) => {
                  // Trigger debounced server-side search
                  debouncedSetAutocompleteSearch(newInputValue);
                }}
                onChange={(event, newValue) => {
                  // Ignore "no results" dummy option
                  if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                    return;
                  }
                  setSelectedClient(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="בחר לקוח (תז/חפ/שם)"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingAutocomplete ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                label="סכום תשלום"
                type="number"
                fullWidth
                sx={{
                  direction: 'ltr',
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '-moz-appearance': 'textfield',
                  },
                }}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
              />
              
              {/* Only show invoice field if Invoice is in transaction fields */}
              {hasInvoiceField && (
                <TextField
                  label="אסמכתא/חשבונית (אופציונלי)"
                  type="text"
                  fullWidth
                  inputProps={{
                    style: { textAlign: 'right', direction: 'ltr' },
                  }}
                  value={invoice}
                  onChange={(e) => setInvoice(e.target.value)}
                />
              )}
              
              <TextField
                label="הערות (אופציונלי)"
                type="text"
                multiline
                minRows={2}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                <DatePicker
                  label="תאריך בפועל"
                  value={actualDate}
                  onChange={(newValue) => setActualDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'התאריך בו בוצעה הפעולה בפועל (ברירת מחדל: היום)',
                    }
                  }}
                />
              </LocalizationProvider>

              {/* Render extra fields if configured */}
              {extraFields.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    שדות נוספים
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {extraFields.map((field) => {
                      const schema = transactionFieldsSchema[field] || {};
                      const isReadOnly = isFieldReadOnly(field);
                      const fieldType = schema.type === 'number' ? 'number' : 'text';
                      if (isReadOnly) {
                        return null;
                      }
                      return (
                        <Box sx={{ width: { xs: '100%', sm: '48%' } }} key={field}>
                          <TextField
                            label={schema.label || field}
                            type={fieldType}
                            value={extraFieldValues[field] || ''}
                            onChange={(e) => handleExtraFieldChange(field, e.target.value)}
                            onFocus={(e) => e.target.select()} 
                            fullWidth
                            disabled={isReadOnly}
                            InputProps={{
                              readOnly: isReadOnly,
                            }}
                            sx={{
                              direction: fieldType === 'number' ? 'ltr' : 'rtl',
                              '& input': {
                                textAlign: 'right',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '-moz-appearance': 'textfield',
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {/* If there's only one category, just show the balance */}
              {singleCategory && selectedClient && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {debtFieldsSchema[singleCategory]?.label || singleCategory} נוכחי: {clientDebt[singleCategory] || 0}
                  </Typography>
                </Box>
              )}

              {/* Show separation section only if there's more than one category */}
              {!singleCategory && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    חלוקה לסעיפים (חייב להתאים לסכום הכולל)
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Dynamically render payment fields based on debt fields configuration */}
                    {debtFields
                      .filter(field => field !== 'TotalDebt')
                      .map(field => (
                        <Box sx={{ width: { xs: '100%', sm: '48%' } }} key={field}>
                          <Typography variant="body2" color="text.secondary">
                            {debtFieldsSchema[field]?.label || field} נוכחי: {clientDebt[field] || 0}
                          </Typography>
                          <TextField
                            label={debtFieldsSchema[field]?.label || field}
                            type="number"
                            value={fieldValues[field]}
                            onChange={(e) => handleFieldChange(field, e.target.value)}
                            onFocus={(e) => e.target.select()}
                            fullWidth
                            sx={{
                              direction: 'ltr',
                              '& input': {
                                textAlign: 'right',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '-moz-appearance': 'textfield',
                              },
                            }}
                          />
                        </Box>
                      ))
                    }
                  </Box>
                </>
              )}

              {/* Payment Methods Section */}
              <PaymentMethodRows
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                totalAmount={parseFloat(amount) || 0}
                compact={false}
              />
            </>
          )}

          {formStep === 'confirm' && (
            <>
              <Typography
                sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}
              >
                אישור תשלום
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                ללקוח: {selectedClient?.ClientName} (
                {selectedClient?.ClientBusinessID})
              </Typography>
              <Typography>סכום: {amount}</Typography>
              {hasInvoiceField && invoice && (
                <Typography>חשבונית: {invoice}</Typography>
              )}
              {notes && (
                <Typography>הערות: {notes}</Typography>
              )}
              {actualDate && (
                <Typography>תאריך בפועל: {actualDate.toLocaleDateString('he-IL')}</Typography>
              )}

              {/* Show extra fields in confirmation if they have values */}
              {extraFields.length > 0 && extraFields.some(field => extraFieldValues[field]) && (
                <>
                  <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                    שדות נוספים:
                  </Typography>
                  {extraFields.map(field => 
                    extraFieldValues[field] ? (
                      <Typography key={field}>
                        {transactionFieldsSchema[field]?.label || field}: {extraFieldValues[field]}
                      </Typography>
                    ) : null
                  )}
                </>
              )}
              
              {/* Only show separation section when there are multiple categories */}
              {!singleCategory && (
                <>
                  <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                    חלוקה:
                  </Typography>
                  {debtFields
                    .filter(field => field !== 'TotalDebt')
                    .map(field => (
                      <Typography key={field}>
                        {debtFieldsSchema[field]?.label || field}: {fieldValues[field] || '0'}
                      </Typography>
                    ))
                  }
                </>
              )}

              {/* Show payment methods in confirmation */}
              {paymentMethods.length > 0 && (
                <>
                  <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                    אמצעי תשלום:
                  </Typography>
                  {paymentMethods.map((pm, index) => {
                    const typeLabels = {
                      cash: 'מזומן',
                      check: 'צ\'ק',
                      credit_card: 'כרטיס אשראי',
                      bank_transfer: 'העברה בנקאית',
                      other: 'אחר'
                    };
                    let details = '';
                    if (pm.type === 'check' && pm.check_number) {
                      details = ` - צ'ק #${pm.check_number}`;
                      if (pm.check_bank_name) details += ` (${pm.check_bank_name})`;
                      if (pm.check_due_date) details += ` לפירעון: ${format(pm.check_due_date, 'dd/MM/yyyy')}`;
                    } else if (pm.type === 'credit_card' && pm.card_last_four) {
                      details = ` - ****${pm.card_last_four}`;
                    } else if (pm.type === 'bank_transfer' && pm.bank_name) {
                      details = ` - ${pm.bank_name}`;
                    }
                    return (
                      <Typography key={index}>
                        {typeLabels[pm.type] || pm.type}: ₪{parseFloat(pm.amount).toFixed(2)}{details}
                      </Typography>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {!localSuccess && (
          <Button
            onClick={handleClose}
            color="inherit"
            size="large"
            sx={{ fontSize: '1rem' }}
          >
            ביטול
          </Button>
        )}
        
        {formStep === 'form' && !localSuccess && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ fontSize: '1rem' }}
            onClick={handleValidation}
          >
            שלב הבא
          </Button>
        )}
        
        {formStep === 'confirm' && !localSuccess && (
          <>
            <Button
              color="inherit"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={() => setFormStep('form')}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                'אישור סופי'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </MobileDialog>
  );
};

export default PaymentDialog;